// AdvancedAnalysis Component - Version 2.1 (Debug boxes removed, theme styling updated)
import React, { useState, useEffect } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import UnifiedDashboard from './UnifiedDashboard';
import ReasoningChain from './ReasoningChain';
import RAGContextDisplay from './RAGContextDisplay';
import AgentOutputView from './AgentOutputView';
import QualityBadge from './QualityBadge';
import QualityTooltip from './QualityTooltip';
import './AdvancedAnalysis.css';
import './QualityBadge.css';
import './QualityTooltip.css';

// Professional markdown renderer using marked and DOMPurify
const MarkdownRenderer = ({ text }) => {
    console.log('[MarkdownRenderer] Component called with text:', text ? `Length: ${text.length}, First 200 chars: ${text.substring(0, 200)}` : 'NULL/EMPTY');
    
    if (!text) {
        console.warn('[MarkdownRenderer] Text is null or empty, returning null');
        return null;
    }
    
    try {
        // Preprocess text to fix markdown formatting issues
        let processedText = text;
        
        // Debug: Log original text (first 500 chars) to see what we're working with
        console.log('[MarkdownRenderer] Original text sample:', processedText.substring(0, 500));
        
        // CRITICAL: First, ensure list items are on separate lines
        // Many LLM outputs have list items embedded in paragraphs like "text. *   **Bold:** more text"
        // We need to split these into proper list format
        
        // Step 1: Normalize all list patterns (both asterisks and dashes) - handle multiple spaces
        // Handle asterisks: "*   " or "*    " -> "* " (at line start)
        processedText = processedText.replace(/^(\s*)\*\s{2,}/gm, '$1* ');
        
        // Handle dashes: "-   " or "-    " -> "- " (at line start) - but not "---" (horizontal rule)
        processedText = processedText.replace(/^(\s*)-\s{2,}(?!-)/gm, '$1- ');
        
        // Step 2: Handle list markers embedded in text (not at line start) - VERY AGGRESSIVE
        // Pattern: "text *   **Bold:**" -> "text\n* **Bold:**"
        // This handles cases where list items are in the middle of paragraphs
        processedText = processedText.replace(/([^\n])\s+([*-])\s{2,}(\*\*[^*]+\*\*)/g, '$1\n$2 $3');
        processedText = processedText.replace(/([^\n])\s+([*-])\s{2,}(\d+\.\s)/g, '$1\n$2 $3');
        
        // Step 2b: Handle list markers with single space (more common in LLM output)
        // Pattern: "text * **Bold:**" -> "text\n* **Bold:**"
        processedText = processedText.replace(/([^\n])\s+([*-])\s+(\*\*[^*]+\*\*)/g, '$1\n$2 $3');
        processedText = processedText.replace(/([^\n])\s+([*-])\s+(\d+\.\s)/g, '$1\n$2 $3');
        
        // Step 2c: Handle list items that start with numbered bold text (e.g., "**1. Text:**")
        // Pattern: "text **1. Text:**" -> "text\n* **1. Text:**"
        // This is common in LLM output where numbered items are bold but not marked as lists
        // Updated regex to match: **1. Text:** (allows any characters including spaces, colons, etc.)
        processedText = processedText.replace(/([^\n])\s+(\*\*\d+\.\s[^*]+?\*\*)/g, '$1\n* $2');
        
        // Step 2d: Handle patterns like "**1. Text:**" at the start of lines (convert to list)
        // If a line starts with "**1. " or "**2. " etc., it's likely a list item
        // Updated regex to match full pattern including colons and any text
        processedText = processedText.replace(/^(\s*)(\*\*\d+\.\s[^*]+?\*\*)/gm, '$1* $2');
        
        // Step 3: Also handle list markers that appear after newlines or periods
        // Pattern: "\n*   " or ". *   " -> "\n* " or ". * "
        processedText = processedText.replace(/(\n|\.)\s*\*\s{2,}/g, '$1\n* ');
        processedText = processedText.replace(/(\n|\.)\s*-\s{2,}(?!-)/g, '$1\n- ');
        // Also handle single space after period/newline
        processedText = processedText.replace(/(\n|\.)\s*\*\s+/g, '$1\n* ');
        processedText = processedText.replace(/(\n|\.)\s*-\s+(?!-)/g, '$1\n- ');
        
        // Step 4: Fix list markers that have no space after them
        // Pattern: "*Text" -> "* Text" (but not "**Text")
        processedText = processedText.replace(/^(\s*)\*(?![*\s])(.+)/gm, '$1* $2');
        // Pattern: "-Text" -> "- Text" (but not "--" or "---")
        processedText = processedText.replace(/^(\s*)-(?![-\s])(.+)/gm, '$1- $2');
        
        // Step 5: Handle numbered lists with list markers (e.g., "* 1. Text" or "- 1. Text")
        // Convert "* 1. " or "- 1. " to just "1. " (numbered lists don't need bullet markers)
        processedText = processedText.replace(/^(\s*)[*-]\s+(\d+\.\s)/gm, '$1$2');
        
        // Step 6: Fix numbered lists - normalize spacing
        processedText = processedText.replace(/^(\s*)(\d+)\.\s{2,}/gm, '$1$2. ');
        
        // Step 7: CRITICAL - Convert numbered bold headings to list items
        // Pattern: Lines starting with "**1. Text:**" should become "* **1. Text:**"
        // This handles the common LLM format where numbered items are bold but not lists
        // Updated regex: non-greedy match to capture text until closing **
        processedText = processedText.replace(/^(\s*)(\*\*\d+\.\s[^*]+?\*\*)/gm, '$1* $2');
        
        // Step 5: Split into lines for additional processing
        const lines = processedText.split('\n');
        const processedLines = [];
        
        for (let i = 0; i < lines.length; i++) {
            let line = lines[i];
            
            // Ensure list items have proper spacing after marker (both * and -)
            // Handle cases like "*   **Bold:**" or "-   **Bold:**" - ensure single space
            if (/^\s*\*\s+\*\*/.test(line)) {
                line = line.replace(/^(\s*)\*\s+(\*\*)/, '$1* $2');
            }
            if (/^\s*-\s+\*\*/.test(line)) {
                line = line.replace(/^(\s*)-\s+(\*\*)/, '$1- $2');
            }
            
            // Fix lines that start with list marker but have inconsistent spacing
            if (/^\s*\*/.test(line) && !/^\s*\*\s/.test(line) && !/^\s*\*\*/.test(line)) {
                // Has asterisk but no space after (and not "**") - add space
                line = line.replace(/^(\s*)\*([^\s*])/, '$1* $2');
            }
            if (/^\s*-/.test(line) && !/^\s*-\s/.test(line) && !/^\s*--/.test(line)) {
                // Has dash but no space after (and not "--") - add space
                line = line.replace(/^(\s*)-([^\s-])/, '$1- $2');
            }
            
            processedLines.push(line);
        }
        
        processedText = processedLines.join('\n');
        
        // Step 6: Final normalization - ensure all list items have exactly one space
        // This catches any remaining edge cases for both * and -
        processedText = processedText.replace(/^(\s*)\*\s{2,}/gm, '$1* ');
        processedText = processedText.replace(/^(\s*)-\s{2,}(?!-)/gm, '$1- ');
        
        // Debug: Log processed text (first 500 chars) to see what we're passing to marked
        console.log('[MarkdownRenderer] Processed text sample:', processedText.substring(0, 500));
        
        // Configure marked for clean output with enhanced list support
        // Use marked.parse() with options for better list handling
        const markedOptions = {
            breaks: true,
            gfm: true,
            headerIds: false,
            mangle: false,
            pedantic: false, // Use GitHub Flavored Markdown rules (more flexible)
            smartLists: true, // Automatically convert lists
            smartypants: false // Don't convert quotes
        };
        
        marked.setOptions(markedOptions);
        
        // Convert markdown to HTML
        const rawMarkup = marked(processedText);
        
        // Debug: Log the generated HTML to see if lists are being created
        console.log('[MarkdownRenderer] Generated HTML sample:', rawMarkup.substring(0, 500));
        // Check if <ul> or <li> tags are present
        if (rawMarkup.includes('<ul>') || rawMarkup.includes('<li>')) {
            console.log('[MarkdownRenderer] ✓ Lists detected in HTML output');
        } else {
            console.warn('[MarkdownRenderer] ⚠ No <ul> or <li> tags found - lists may not be rendering');
            // Additional debug: show what patterns we're looking for
            console.log('[MarkdownRenderer] Checking for list patterns in processed text...');
            const lines = processedText.split('\n');
            const asteriskLines = lines.filter(line => /^\s*\*\s/.test(line));
            const dashLines = lines.filter(line => /^\s*-\s/.test(line));
            console.log('[MarkdownRenderer] Has lines starting with "* ":', asteriskLines.length > 0, `(${asteriskLines.length} lines)`);
            console.log('[MarkdownRenderer] Has lines starting with "- ":', dashLines.length > 0, `(${dashLines.length} lines)`);
            if (asteriskLines.length > 0) {
                console.log('[MarkdownRenderer] Sample asterisk lines:', asteriskLines.slice(0, 3));
            }
            if (dashLines.length > 0) {
                console.log('[MarkdownRenderer] Sample dash lines:', dashLines.slice(0, 3));
            }
        }
        
        // Sanitize HTML to prevent XSS
        const cleanMarkup = DOMPurify.sanitize(rawMarkup, {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a'],
            ALLOWED_ATTR: ['href', 'target', 'rel']
        });
        
        return (
            <div 
                className="markdown-content"
                dangerouslySetInnerHTML={{ __html: cleanMarkup }}
            />
        );
    } catch (error) {
        console.warn('Markdown rendering error, falling back to plain text:', error);
        // Fallback to plain text with basic formatting
        return (
            <div className="markdown-content">
                {text.split('\n').map((line, i) => (
                    <p key={i}>{line || '\u00A0'}</p>
                ))}
            </div>
        );
    }
};

const AdvancedAnalysis = ({ 
    advancedAnalysis = {}, 
    ragAnalysis = null, 
    agentMetadata = null,
    resumeId = null 
}) => {
    // Debug: Log agentMetadata structure
    React.useEffect(() => {
        if (agentMetadata) {
            console.log('[AdvancedAnalysis] agentMetadata received:', {
                hasAgentMetadata: !!agentMetadata,
                agentMetadataKeys: Object.keys(agentMetadata),
                hasAuditTrail: !!agentMetadata.audit_trail,
                auditTrailType: typeof agentMetadata.audit_trail,
                auditTrailIsObject: agentMetadata.audit_trail && typeof agentMetadata.audit_trail === 'object',
                auditTrailKeys: agentMetadata.audit_trail ? Object.keys(agentMetadata.audit_trail).slice(0, 30) : [],
                // Check for enhanced fields
                hasJDExtractionDecisions: !!(agentMetadata.audit_trail?.jd_extraction_decisions),
                jdExtractionDecisionsCount: agentMetadata.audit_trail?.jd_extraction_decisions?.length || 0,
                hasEvaluationModeDecision: !!agentMetadata.audit_trail?.evaluation_mode_decision,
                hasScoreCalculationDetails: !!agentMetadata.audit_trail?.score_calculation_details,
                hasSubstitutabilityDecisions: !!(agentMetadata.audit_trail?.substitutability_decisions),
                substitutabilityDecisionsCount: agentMetadata.audit_trail?.substitutability_decisions?.length || 0,
                hasParallelExecutionDecisions: !!agentMetadata.audit_trail?.parallel_execution_decisions,
                resumeId: resumeId
            });
        } else {
            console.log('[AdvancedAnalysis] agentMetadata is null/undefined');
        }
    }, [agentMetadata, resumeId]);

    // Extract overall comment and call callback if provided
    React.useEffect(() => {
        if (advancedAnalysis.detailed_reasoning && typeof advancedAnalysis.detailed_reasoning === "object") {
            const overallCommentKey = Object.keys(advancedAnalysis.detailed_reasoning).find(key => 
                key === "overall_comment"
            );
            if (overallCommentKey) {
                const overallData = advancedAnalysis.detailed_reasoning[overallCommentKey];
                let overallCommentContent = null;
                if (typeof overallData === "object" && (overallData.overall_comment || overallData.comment)) {
                    overallCommentContent = (overallData.overall_comment || overallData.comment);
                } else if (typeof overallData === "string") {
                    overallCommentContent = overallData;
                }
                if (overallCommentContent && window.setOverallComment) {
                    window.setOverallComment(overallCommentContent);
                }
            }
        }
    }, [advancedAnalysis.detailed_reasoning]);

    // Helper functions
    const formatDetailedReasoning = (reasoning) => {
        // Filter out Overall comment section from display
        if (!reasoning) return null;
        
        let parsedReasoning;
        if (typeof reasoning === 'string') {
            try {
                parsedReasoning = JSON.parse(reasoning);
            } catch (e) {
                return <div className="reasoning-text">{reasoning}</div>;
            }
        } else {
            parsedReasoning = reasoning;
        }
        
        const filteredReasoning = { ...parsedReasoning };
        if (filteredReasoning) {
            const overallCommentKey = Object.keys(filteredReasoning).find(key => 
                key === "overall_comment"
            );
            if (overallCommentKey) {
                delete filteredReasoning[overallCommentKey];
            }
        }

        return (
            <div className="reasoning-sections">
                {Object.entries(filteredReasoning).map(([section, data]) => (
                    <div key={section} className="reasoning-section">
                        <h4 className="section-name">{section.charAt(0).toUpperCase() + section.slice(1).replace(/_/g, " ")}:</h4>
                        <p className="section-comment">{data.comment || "No comment available"}</p>
                    </div>
                ))}
            </div>
        );
    };

    // Removed unused function - formatFinalScore

    // Check if we have any data to display
    const hasAdvancedAnalysis = advancedAnalysis && Object.keys(advancedAnalysis).length > 0;
    const hasRAGAnalysis = ragAnalysis && Object.keys(ragAnalysis).length > 0;
    const hasAgentMetadata = agentMetadata && Object.keys(agentMetadata).length > 0;
    const hasAnyData = hasAdvancedAnalysis || hasRAGAnalysis || hasAgentMetadata;
    
    return (
        <div className="advanced-analysis" data-component-version="2.0">
            {!hasAnyData ? null : (
                <div className="advanced-content">

                    {/* Unified Dashboard: ALWAYS RENDERED */}
                    <div className="analysis-item">
                        <h3 className="analysis-subtitle">📊 Section Analysis Dashboard</h3>
                        <UnifiedDashboard 
                            sectionWeights={advancedAnalysis.section_weights || {}} 
                            subfieldScores={advancedAnalysis.subfield_scores || {}} 
                            detailedReasoning={advancedAnalysis.detailed_reasoning || {}}
                        />
                    </div>

                    {/* Detailed Comments & Reasoning */}
                    {advancedAnalysis.detailed_reasoning && (
                        <div className="analysis-item">
                            <h3 className="analysis-subtitle">💬 Detailed Comments & Reasoning</h3>
                            {formatDetailedReasoning(advancedAnalysis.detailed_reasoning)}
                        </div>
                    )}

                    {/* Overall Assessment - Legacy fallback only */}
                    {advancedAnalysis.overall_assessment && !advancedAnalysis.summary_perspectives?.combined_summary && (
                        <div className="analysis-item">
                            <h3 className="analysis-subtitle">🎯 Overall Assessment</h3>
                            <div className="overall-assessment">
                                {typeof advancedAnalysis.overall_assessment === 'string' ? (
                                    <div className="overall-assessment-text">{advancedAnalysis.overall_assessment}</div>
                                ) : (
                                    <div className="overall-assessment-structured">
                                        {advancedAnalysis.overall_assessment.strengths && advancedAnalysis.overall_assessment.strengths.length > 0 && (
                                            <div className="assessment-section">
                                                <h4 className="assessment-subtitle">✅ Key Strengths</h4>
                                                <ul className="assessment-list">
                                                    {advancedAnalysis.overall_assessment.strengths.map((strength, index) => (
                                                        <li key={index} className="assessment-item">{strength}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {advancedAnalysis.overall_assessment.Shortfall_Areas && advancedAnalysis.overall_assessment.Shortfall_Areas.length > 0 && (
                                            <div className="assessment-section">
                                                <h4 className="assessment-subtitle">⚠️ Shortfall Areas</h4>
                                                <ul className="assessment-list">
                                                    {advancedAnalysis.overall_assessment.Shortfall_Areas.map((area, index) => (
                                                        <li key={index} className="assessment-item">{area}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {advancedAnalysis.overall_assessment.cultural_fit && (
                                            <div className="assessment-section">
                                                <h4 className="assessment-subtitle">🤝 Cultural Fit</h4>
                                                <p className="assessment-text">{advancedAnalysis.overall_assessment.cultural_fit}</p>
                                            </div>
                                        )}
                                        {advancedAnalysis.overall_assessment.growth_potential && (
                                            <div className="assessment-section">
                                                <h4 className="assessment-subtitle">📈 Growth Potential</h4>
                                                <p className="assessment-text">{advancedAnalysis.overall_assessment.growth_potential}</p>
                                            </div>
                                        )}
                                        {advancedAnalysis.overall_assessment.risk_factors && (
                                            <div className="assessment-section">
                                                <h4 className="assessment-subtitle">⚠️ Risk Factors</h4>
                                                <p className="assessment-text">{advancedAnalysis.overall_assessment.risk_factors}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                </div>
            )}

            {/* Multi-Perspective Summaries (SummarizerAgent Output) */}
            {(() => {
                // Check multiple locations for summary_perspectives:
                // 1. advancedAnalysis.summary_perspectives (legacy format - most common)
                // 2. ragAnalysis.summary_perspectives (if stored directly in rag_analysis)
                // 3. Parse ragAnalysis if it's a JSON string
                let parsedRagAnalysis = ragAnalysis;
                if (typeof ragAnalysis === 'string') {
                    try {
                        parsedRagAnalysis = JSON.parse(ragAnalysis);
                    } catch (e) {
                        console.warn('[AdvancedAnalysis] Failed to parse ragAnalysis as JSON:', e);
                    }
                }
                
                const summaryPerspectives = advancedAnalysis?.summary_perspectives 
                    || parsedRagAnalysis?.summary_perspectives 
                    || ragAnalysis?.summary_perspectives;
                
                console.log('[AdvancedAnalysis] Checking summary perspectives:', {
                    hasRagAnalysis: !!ragAnalysis,
                    ragAnalysisType: typeof ragAnalysis,
                    hasAdvancedAnalysis: !!advancedAnalysis,
                    ragSummaryPerspectives: parsedRagAnalysis?.summary_perspectives ? Object.keys(parsedRagAnalysis.summary_perspectives) : null,
                    advancedSummaryPerspectives: advancedAnalysis?.summary_perspectives ? Object.keys(advancedAnalysis.summary_perspectives) : null,
                    finalSummaryPerspectives: summaryPerspectives ? Object.keys(summaryPerspectives) : null,
                    hasCombinedSummary: !!summaryPerspectives?.combined_summary,
                    combinedSummaryLength: summaryPerspectives?.combined_summary?.length || 0,
                    allAdvancedAnalysisKeys: advancedAnalysis ? Object.keys(advancedAnalysis) : null
                });
                
                return summaryPerspectives && (
                    <MultiPerspectiveSection summaryPerspectives={summaryPerspectives} />
                );
            })()}

            {/* RAG Explainability Section (ExplainabilityAgent Output) */}
            {/* Always show section, even if no data - UI should be visible regardless */}
            <RAGExplainabilitySection 
                auditTrail={agentMetadata?.audit_trail || null}
                processingTimeline={agentMetadata?.processing_timeline || []}
                totalProcessingTime={agentMetadata?.total_processing_time_ms || null}
                errors={agentMetadata?.errors || {}}
                qualityJudgments={agentMetadata?.quality_judgments || {}}
            />
        </div>
    );
};

// Multi-Perspective Section Component (Tabbed Interface)
const MultiPerspectiveSection = ({ summaryPerspectives }) => {
    const [activePerspective, setActivePerspective] = useState('cto'); // CTO is default
    
    const hasCTO = !!summaryPerspectives?.cto_perspective;
    const hasHR = !!summaryPerspectives?.hr_perspective;
    const hasCEO = !!summaryPerspectives?.ceo_perspective;
    
    // If no perspectives available, don't render
    if (!hasCTO && !hasHR && !hasCEO) {
        return null;
    }
    
    return (
        <div className="rag-section multi-perspective-summaries">
            <h3 className="rag-section-title">👥 Multi-Perspective Analysis</h3>
            
            {/* Tab Buttons */}
            <div className="explainability-tabs">
                {hasCTO && (
                    <button 
                        className={`tab-button ${activePerspective === 'cto' ? 'active' : ''}`}
                        onClick={() => setActivePerspective('cto')}
                    >
                        🔧 CTO
                    </button>
                )}
                {hasHR && (
                    <button 
                        className={`tab-button ${activePerspective === 'hr' ? 'active' : ''}`}
                        onClick={() => setActivePerspective('hr')}
                    >
                        👔 HR
                    </button>
                )}
                {hasCEO && (
                    <button 
                        className={`tab-button ${activePerspective === 'ceo' ? 'active' : ''}`}
                        onClick={() => setActivePerspective('ceo')}
                    >
                        💼 CEO
                    </button>
                )}
            </div>
            
            {/* Tab Content */}
            <div className="tab-content-wrapper">
                {activePerspective === 'cto' && hasCTO && (
                    <div className="tab-content">
                        <h4>🔧 CTO Perspective</h4>
                        <div className="perspective-content">
                            <MarkdownRenderer text={summaryPerspectives.cto_perspective} />
                        </div>
                    </div>
                )}
                
                {activePerspective === 'hr' && hasHR && (
                    <div className="tab-content">
                        <h4>👔 HR Perspective</h4>
                        <div className="perspective-content">
                            <MarkdownRenderer text={summaryPerspectives.hr_perspective} />
                        </div>
                    </div>
                )}
                
                {activePerspective === 'ceo' && hasCEO && (
                    <div className="tab-content">
                        <h4>💼 CEO Perspective</h4>
                        <div className="perspective-content">
                            <MarkdownRenderer text={summaryPerspectives.ceo_perspective} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// RAG Explainability Section Component
const RAGExplainabilitySection = ({ 
    auditTrail, 
    processingTimeline = [], 
    totalProcessingTime = null,
    errors = {},
    qualityJudgments = {}
}) => {
    // #region debug log
    console.log('[RAGExplainabilitySection] Component function called', {
        hasAuditTrail: !!auditTrail,
        processingTimelineLength: processingTimeline?.length || 0,
        totalProcessingTime,
        errorsCount: Object.keys(errors || {}).length
    });
    // #endregion
    
    const [activeTab, setActiveTab] = useState('overview');
    
    // Extract data from audit trail
    const scoreExplanations = auditTrail?.score_explanations || [];
    // Filter score explanations that have reasoning chains
    // Pass the full ScoreExplanation object to ReasoningChain component
    // ReasoningChain expects explanation.reasoning_chain to exist
    const reasoningChains = scoreExplanations.filter(exp => exp.reasoning_chain);
    const ragContextReferences = auditTrail?.rag_context_references || [];
    const agentOutputs = auditTrail?.agent_outputs || [];
    const decisionPoints = auditTrail?.decision_points || [];
    // Enhanced explainability fields
    const jdExtractionDecisions = auditTrail?.jd_extraction_decisions || [];
    const evaluationModeDecision = auditTrail?.evaluation_mode_decision;
    const scoreCalculationDetails = auditTrail?.score_calculation_details;
    const substitutabilityDecisions = auditTrail?.substitutability_decisions || [];
    const parallelExecutionDecisions = auditTrail?.parallel_execution_decisions;
    
    // Debug: Log auditTrail structure when it changes
    useEffect(() => {
        console.log('[RAGExplainabilitySection] auditTrail received:', {
            hasAuditTrail: !!auditTrail,
            auditTrailType: typeof auditTrail,
            auditTrailKeys: auditTrail ? Object.keys(auditTrail).slice(0, 30) : [],
            // Enhanced fields check
            hasJDExtractionDecisions: !!(auditTrail?.jd_extraction_decisions),
            jdExtractionDecisionsCount: auditTrail?.jd_extraction_decisions?.length || 0,
            hasEvaluationModeDecision: !!auditTrail?.evaluation_mode_decision,
            evaluationModeDecisionType: auditTrail?.evaluation_mode_decision ? typeof auditTrail.evaluation_mode_decision : null,
            evaluationModeDecisionKeys: auditTrail?.evaluation_mode_decision ? Object.keys(auditTrail.evaluation_mode_decision) : [],
            hasScoreCalculationDetails: !!auditTrail?.score_calculation_details,
            scoreCalculationDetailsType: auditTrail?.score_calculation_details ? typeof auditTrail.score_calculation_details : null,
            scoreCalculationDetailsKeys: auditTrail?.score_calculation_details ? Object.keys(auditTrail.score_calculation_details) : [],
            hasSubstitutabilityDecisions: !!(auditTrail?.substitutability_decisions),
            substitutabilityDecisionsCount: auditTrail?.substitutability_decisions?.length || 0,
            hasParallelExecutionDecisions: !!auditTrail?.parallel_execution_decisions
        });
    }, [auditTrail]);
    
    // Debug logging
    useEffect(() => {
        if (auditTrail) {
            console.log('[RAG Explainability] Audit trail data:', {
                hasScoreExplanations: scoreExplanations.length > 0,
                hasReasoningChains: reasoningChains.length > 0,
                hasRAGContext: ragContextReferences.length > 0,
                hasAgentOutputs: agentOutputs.length > 0,
                scoreExplanationsCount: scoreExplanations.length,
                reasoningChainsCount: reasoningChains.length,
                ragContextCount: ragContextReferences.length,
                // Enhanced explainability fields
                hasJDExtractionDecisions: !!(auditTrail.jd_extraction_decisions && auditTrail.jd_extraction_decisions.length > 0),
                jdExtractionDecisionsCount: auditTrail.jd_extraction_decisions?.length || 0,
                hasEvaluationModeDecision: !!auditTrail.evaluation_mode_decision,
                evaluationModeDecision: auditTrail.evaluation_mode_decision ? {
                    selected_mode: auditTrail.evaluation_mode_decision.selected_mode,
                    confidence: auditTrail.evaluation_mode_decision.confidence
                } : null,
                hasScoreCalculationDetails: !!auditTrail.score_calculation_details,
                scoreCalculationDetails: auditTrail.score_calculation_details ? {
                    final_score: auditTrail.score_calculation_details.final_score,
                    has_dimension_weights: !!auditTrail.score_calculation_details.dimension_weights,
                    has_calculation_steps: !!(auditTrail.score_calculation_details.calculation_steps && auditTrail.score_calculation_details.calculation_steps.length > 0)
                } : null,
                hasSubstitutabilityDecisions: !!(auditTrail.substitutability_decisions && auditTrail.substitutability_decisions.length > 0),
                substitutabilityDecisionsCount: auditTrail.substitutability_decisions?.length || 0,
                hasParallelExecutionDecisions: !!auditTrail.parallel_execution_decisions,
                parallelExecutionDecisions: auditTrail.parallel_execution_decisions ? {
                    parallel_enabled: auditTrail.parallel_execution_decisions.parallel_enabled,
                    time_saved_ms: auditTrail.parallel_execution_decisions.time_saved_ms
                } : null,
                allAuditTrailKeys: Object.keys(auditTrail),
                sampleReasoningChain: reasoningChains.length > 0 ? {
                    dimension: reasoningChains[0].dimension,
                    hasReasoningChain: !!reasoningChains[0].reasoning_chain,
                    reasoningChainKeys: reasoningChains[0].reasoning_chain ? Object.keys(reasoningChains[0].reasoning_chain) : []
                } : null
            });
        }
    }, [auditTrail, scoreExplanations, reasoningChains, ragContextReferences, agentOutputs]);

    // Show error message if audit_trail is missing but we have errors
    const hasExplainabilityError = errors && errors.ExplainabilityAgent;
    
    // Debug: Show data availability indicator
    const hasEnhancedData = evaluationModeDecision || scoreCalculationDetails || jdExtractionDecisions.length > 0 || substitutabilityDecisions.length > 0 || parallelExecutionDecisions;
    
    // #region debug log
    useEffect(() => {
        fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AdvancedAnalysis.js:594',message:'RAGExplainabilitySection rendering',data:{hasAuditTrail:!!auditTrail,activeTab,hasEnhancedData,jdExtractionDecisionsCount:jdExtractionDecisions.length,hasEvaluationModeDecision:!!evaluationModeDecision,hasScoreCalculationDetails:!!scoreCalculationDetails,substitutabilityDecisionsCount:substitutabilityDecisions.length,hasParallelExecutionDecisions:!!parallelExecutionDecisions},timestamp:Date.now(),sessionId:'debug-session',runId:'tab-visibility-check',hypothesisId:'A'})}).catch(()=>{});
    }, [auditTrail, activeTab, hasEnhancedData, jdExtractionDecisions.length, evaluationModeDecision, scoreCalculationDetails, substitutabilityDecisions.length, parallelExecutionDecisions]);
    // #endregion
    
    // #region debug log
    useEffect(() => {
        // Check if tabs are in DOM after render
        setTimeout(() => {
            const tabContainer = document.querySelector('.explainability-tabs');
            const tabButtons = tabContainer ? Array.from(tabContainer.querySelectorAll('.tab-button')) : [];
            const enhancedTabs = tabButtons.filter(btn => {
                const text = btn.textContent || '';
                return text.includes('JD Extraction') || text.includes('Evaluation Mode') || text.includes('Score Calculation') || text.includes('Substitutability') || text.includes('Parallel Execution');
            });
            fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AdvancedAnalysis.js:607',message:'DOM check for enhanced tabs',data:{hasTabContainer:!!tabContainer,totalTabButtons:tabButtons.length,enhancedTabsCount:enhancedTabs.length,enhancedTabTexts:enhancedTabs.map(btn => btn.textContent?.trim())},timestamp:Date.now(),sessionId:'debug-session',runId:'tab-visibility-check',hypothesisId:'B'})}).catch(()=>{});
        }, 100);
    }, [activeTab]);
    // #endregion
    
    // #region debug log
    console.log('[RAGExplainabilitySection] About to return JSX', {
        hasAuditTrail: !!auditTrail,
        willRender: true
    });
    // #endregion
    
    return (
        <div className="rag-section explainability-section">
            <h3 className="rag-section-title">Resume Analysis Explainability & Audit Trail</h3>
            
            {/* Debug: Show enhanced data availability */}
            {hasEnhancedData && (
                <div className="enhanced-data-indicator" style={{
                    padding: '1rem',
                    marginBottom: '1rem',
                    background: 'rgba(0, 170, 255, 0.1)',
                    backdropFilter: 'blur(8px) saturate(150%)',
                    WebkitBackdropFilter: 'blur(8px) saturate(150%)',
                    border: '1px solid rgba(0, 170, 255, 0.3)',
                    borderRadius: '12px',
                    color: 'var(--text-color)',
                    fontSize: '0.9em'
                }}>
                    <strong style={{color: 'var(--primary-color)'}}>✅ Enhanced Explainability Data Available:</strong>
                    <ul style={{margin: '0.5rem 0 0 1.5rem', padding: 0, color: 'var(--text-color)'}}>
                        {evaluationModeDecision && <li>🎯 Evaluation Mode Decision</li>}
                        {scoreCalculationDetails && <li>🧮 Score Calculation Details</li>}
                        {jdExtractionDecisions.length > 0 && <li>📋 JD Extraction Decisions ({jdExtractionDecisions.length})</li>}
                        {substitutabilityDecisions.length > 0 && <li>🔄 Substitutability Decisions ({substitutabilityDecisions.length})</li>}
                        {parallelExecutionDecisions && <li>⚡ Parallel Execution Decision</li>}
                    </ul>
                </div>
            )}
            {!hasEnhancedData && auditTrail && (
                <div style={{
                    padding: '1rem',
                    marginBottom: '1rem',
                    background: 'rgba(255, 193, 7, 0.1)',
                    backdropFilter: 'blur(8px) saturate(150%)',
                    WebkitBackdropFilter: 'blur(8px) saturate(150%)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    borderRadius: '12px',
                    color: 'var(--text-color)',
                    fontSize: '0.9em'
                }}>
                    <strong style={{color: '#ffc107'}}>⚠️ No Enhanced Explainability Data:</strong> Check console logs for details. auditTrail keys: {Object.keys(auditTrail).join(', ')}
                </div>
            )}
            
            {/* Show error if ExplainabilityAgent failed */}
            {hasExplainabilityError && !auditTrail && (
                <div className="error-message" style={{padding: '1rem', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', marginBottom: '1rem'}}>
                    <strong>⚠️ Explainability Agent Error:</strong>
                    <p style={{margin: '0.5rem 0'}}>{errors.ExplainabilityAgent}</p>
                    <p style={{fontSize: '0.9em', color: '#666', margin: '0.5rem 0 0 0'}}>
                        Partial explainability data (timeline, errors) is shown below.
                    </p>
                </div>
            )}
            
            {/* Processing Timeline Summary */}
            {processingTimeline && processingTimeline.length > 0 && (
                <div className="processing-timeline-summary">
                    <h4>⏱️ Processing Timeline</h4>
                    <div className="timeline-items">
                        {processingTimeline.map((item, idx) => {
                            const durationSeconds = item.duration_ms ? (item.duration_ms / 1000).toFixed(2) : '0.00';
                            const agentName = item.agent || item.agent_name || 'Unknown';
                            const qualityJudgment = qualityJudgments[agentName];
                            return (
                                <div key={idx} className="timeline-item">
                                    <span className="agent-name">{agentName}</span>
                                    <span className={`status ${item.success ? 'success' : 'error'}`}>
                                        {item.success ? '✓' : '✗'}
                                    </span>
                                    {qualityJudgment && (
                                        <QualityTooltip judgment={qualityJudgment}>
                                            <QualityBadge judgment={qualityJudgment} agentName={agentName} />
                                        </QualityTooltip>
                                    )}
                                    <span className="duration">{durationSeconds}s</span>
                                </div>
                            );
                        })}
                    </div>
                    {totalProcessingTime && (
                        <div className="total-time">
                            <strong>Total Processing Time: {(totalProcessingTime / 1000).toFixed(2)}s</strong>
                        </div>
                    )}
                </div>
            )}

            {/* Tab Navigation */}
            <div className="explainability-tabs">
                {/* #region debug log */}
                {(() => {
                    console.log('[RAGExplainabilitySection] Rendering tabs - Enhanced tabs should be visible:', {
                        activeTab,
                        jdExtractionDecisionsCount: jdExtractionDecisions.length,
                        hasEvaluationModeDecision: !!evaluationModeDecision,
                        hasScoreCalculationDetails: !!scoreCalculationDetails,
                        substitutabilityDecisionsCount: substitutabilityDecisions.length,
                        hasParallelExecutionDecisions: !!parallelExecutionDecisions
                    });
                    fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'AdvancedAnalysis.js:662',message:'Rendering tab navigation',data:{activeTab,jdExtractionDecisionsCount:jdExtractionDecisions.length,hasEvaluationModeDecision:!!evaluationModeDecision,hasScoreCalculationDetails:!!scoreCalculationDetails,substitutabilityDecisionsCount:substitutabilityDecisions.length,hasParallelExecutionDecisions:!!parallelExecutionDecisions},timestamp:Date.now(),sessionId:'debug-session',runId:'tab-visibility-check',hypothesisId:'C'})}).catch(()=>{});
                    return null;
                })()}
                {/* #endregion */}
                <button 
                    className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button 
                    className={`tab-button ${activeTab === 'reasoning' ? 'active' : ''}`}
                    onClick={() => setActiveTab('reasoning')}
                >
                    Reasoning Chains
                </button>
                <button 
                    className={`tab-button ${activeTab === 'rag' ? 'active' : ''}`}
                    onClick={() => setActiveTab('rag')}
                >
                    RAG Context
                </button>
                <button 
                    className={`tab-button ${activeTab === 'agents' ? 'active' : ''}`}
                    onClick={() => setActiveTab('agents')}
                >
                    Agent Outputs
                </button>
                {decisionPoints && decisionPoints.length > 0 && (
                    <button 
                        className={`tab-button ${activeTab === 'decisions' ? 'active' : ''}`}
                        onClick={() => setActiveTab('decisions')}
                    >
                        Decisions
                    </button>
                )}
                {/* Always show enhanced explainability tabs, even if data is missing */}
                <button 
                    className={`tab-button ${activeTab === 'jd-extraction' ? 'active' : ''}`}
                    onClick={() => setActiveTab('jd-extraction')}
                    style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
                >
                    📋 JD Extraction{jdExtractionDecisions.length > 0 && ` (${jdExtractionDecisions.length})`}
                </button>
                <button 
                    className={`tab-button ${activeTab === 'evaluation-mode' ? 'active' : ''}`}
                    onClick={() => setActiveTab('evaluation-mode')}
                    style={{whiteSpace: 'nowrap'}}
                >
                    🎯 Evaluation Mode{evaluationModeDecision ? ' ✓' : ''}
                </button>
                <button 
                    className={`tab-button ${activeTab === 'score-calculation' ? 'active' : ''}`}
                    onClick={() => setActiveTab('score-calculation')}
                    style={{whiteSpace: 'nowrap'}}
                >
                    🧮 Score Calculation{scoreCalculationDetails ? ' ✓' : ''}
                </button>
                <button 
                    className={`tab-button ${activeTab === 'substitutability' ? 'active' : ''}`}
                    onClick={() => setActiveTab('substitutability')}
                    style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
                >
                    🔄 Substitutability{substitutabilityDecisions.length > 0 && ` (${substitutabilityDecisions.length})`}
                </button>
                <button 
                    className={`tab-button ${activeTab === 'parallel-execution' ? 'active' : ''}`}
                    onClick={() => setActiveTab('parallel-execution')}
                    style={{whiteSpace: 'nowrap'}}
                >
                    ⚡ Parallel Execution{parallelExecutionDecisions ? ' ✓' : ''}
                </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content-wrapper">
                {activeTab === 'overview' && (
                    <div className="tab-content">
                        <h4>Evaluation Overview</h4>
                        <p className="info-text">
                            <strong>📌 Note:</strong> The text you see above in "Unified Candidate Assessment" is from the <strong>SummarizerAgent</strong> (combined summary). 
                            The <strong>ExplainabilityAgent</strong> data is shown in this section below, including score explanations, reasoning chains, RAG context, and agent outputs.
                        </p>
                        {auditTrail?.evaluation_id && (
                            <p><strong>Evaluation ID:</strong> {auditTrail.evaluation_id}</p>
                        )}
                        {scoreExplanations.length > 0 && (
                            <div className="score-explanations-summary">
                                <h5>Score Explanations by Dimension (ExplainabilityAgent Output)</h5>
                                <p className="info-text">These detailed score explanations show how each dimension was evaluated with step-by-step reasoning:</p>
                                {scoreExplanations.map((explanation, idx) => (
                                    <div key={idx} className="score-explanation-item">
                                        <strong>{explanation.dimension || 'Unknown'}:</strong> {explanation.score || 'N/A'}/100
                                        {explanation.explanation && (
                                            <p className="explanation-text">{explanation.explanation}</p>
                                        )}
                                        {explanation.reasoning_chain && (
                                            <p className="reasoning-note">✓ Detailed reasoning chain available in "Reasoning Chains" tab</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {Object.keys(errors).length > 0 && (
                            <div className="errors-section">
                                <h5>⚠️ Errors</h5>
                                {Object.entries(errors).map(([agent, error]) => (
                                    <div key={agent} className="error-item">
                                        <strong>{agent}:</strong> {error}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'reasoning' && (
                    <div className="tab-content">
                        <h4>Reasoning Chains</h4>
                        {reasoningChains.length === 0 ? (
                            <p className="no-data-message">No reasoning chains available</p>
                        ) : (
                            reasoningChains.map((explanation, idx) => (
                                <ReasoningChain
                                    key={idx}
                                    explanation={explanation}
                                    dimension={explanation.dimension || 'General'}
                                />
                            ))
                        )}
                    </div>
                )}

                {activeTab === 'rag' && (
                    <div className="tab-content">
                        <h4>RAG Context References</h4>
                        {ragContextReferences.length === 0 ? (
                            <p className="no-data-message">No RAG context references available</p>
                        ) : (
                            <RAGContextDisplay references={ragContextReferences} />
                        )}
                    </div>
                )}

                {activeTab === 'agents' && (
                    <div className="tab-content">
                        <h4>Agent Outputs</h4>
                        {agentOutputs.length === 0 ? (
                            <p className="no-data-message">No agent outputs available</p>
                        ) : (
                            <AgentOutputView outputs={agentOutputs} />
                        )}
                    </div>
                )}

                {activeTab === 'decisions' && (
                    <div className="tab-content">
                        <h4>Decision Points</h4>
                        {decisionPoints.length === 0 ? (
                            <p className="no-data-message">No decision points available</p>
                        ) : (
                            <div className="decisions-list">
                                {decisionPoints.map((decision, idx) => (
                                    <div key={idx} className="decision-item">
                                        <h5>{decision.decision_type || 'Decision'}</h5>
                                        <p>{decision.description || decision.reasoning || 'No description'}</p>
                                        {decision.confidence && (
                                            <span className="confidence">Confidence: {decision.confidence}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'jd-extraction' && (
                    <div className="tab-content">
                        <h4>JD Requirement Extraction Decisions</h4>
                        {jdExtractionDecisions.length === 0 ? (
                            <p className="no-data-message">No JD extraction decisions available</p>
                        ) : (
                            <div className="decisions-list">
                                {jdExtractionDecisions.map((decision, idx) => (
                                    <div key={idx} className="decision-item">
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem'}}>
                                            <strong>{decision.requirement_text?.substring(0, 80)}...</strong>
                                            <span className={`badge ${decision.decision === 'accepted' ? 'success' : decision.decision === 'rejected' ? 'error' : 'warning'}`}>
                                                {decision.decision}
                                            </span>
                                        </div>
                                        <p><strong>Dimension:</strong> {decision.dimension}</p>
                                        <p><strong>Validation Status:</strong> {decision.validation_status}</p>
                                        {decision.reasoning && <p><strong>Reasoning:</strong> {decision.reasoning}</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'evaluation-mode' && (
                    <div className="tab-content">
                        <h4>Evaluation Mode Decision</h4>
                        {!evaluationModeDecision ? (
                            <p className="no-data-message">No evaluation mode decision available</p>
                        ) : (
                            <div className="decision-item">
                                <p><strong>Selected Mode:</strong> {evaluationModeDecision.selected_mode}</p>
                                <p><strong>Confidence:</strong> {(evaluationModeDecision.confidence * 100).toFixed(1)}%</p>
                                {evaluationModeDecision.reasoning && (
                                    <p><strong>Reasoning:</strong> {evaluationModeDecision.reasoning}</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'score-calculation' && (
                    <div className="tab-content">
                        <h4>Score Calculation Details</h4>
                        {!scoreCalculationDetails ? (
                            <p className="no-data-message">No score calculation details available</p>
                        ) : (
                            <div className="decision-item">
                                <p><strong>Final Score:</strong> {scoreCalculationDetails.final_score?.toFixed(1)}</p>
                                {scoreCalculationDetails.dimension_weights && (
                                    <div>
                                        <strong>Dimension Weights:</strong>
                                        <ul>
                                            {Object.entries(scoreCalculationDetails.dimension_weights).map(([dim, weight]) => (
                                                <li key={dim}>{dim}: {(weight * 100).toFixed(1)}%</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {scoreCalculationDetails.formula && (
                                    <p><strong>Formula:</strong> <code>{scoreCalculationDetails.formula}</code></p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'substitutability' && (
                    <div className="tab-content">
                        <h4>Substitutability Decisions</h4>
                        {substitutabilityDecisions.length === 0 ? (
                            <p className="no-data-message">No substitutability decisions available</p>
                        ) : (
                            <div className="decisions-list">
                                {substitutabilityDecisions.map((decision, idx) => (
                                    <div key={idx} className="decision-item">
                                        <p><strong>{decision.candidate_skill}</strong> → <strong>{decision.required_skill}</strong></p>
                                        <p><strong>Substitutable:</strong> {decision.substitutable ? 'Yes' : 'No'}</p>
                                        <p><strong>Reasoning:</strong> {decision.reasoning}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'parallel-execution' && (
                    <div className="tab-content">
                        <h4>Parallel Execution Decision</h4>
                        {!parallelExecutionDecisions ? (
                            <p className="no-data-message">No parallel execution decision available</p>
                        ) : (
                            <div className="decision-item">
                                <p><strong>Parallel Enabled:</strong> {parallelExecutionDecisions.parallel_enabled ? 'Yes' : 'No'}</p>
                                {parallelExecutionDecisions.parallel_enabled && (
                                    <>
                                        <p><strong>Time Saved:</strong> {(parallelExecutionDecisions.time_saved_ms / 1000).toFixed(2)}s ({parallelExecutionDecisions.time_saved_percent?.toFixed(1)}%)</p>
                                        <p><strong>Reasoning:</strong> {parallelExecutionDecisions.reasoning}</p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdvancedAnalysis;
